/**
 * MCP control plane (EpiDevMCP/docs/control-plane-plan.md §4.6). Shapes come from the
 * generated contract; this module aliases them (matching `contract.ts`'s single
 * re-export convention) and holds the request functions. Later rows add their calls
 * here (policy, seats, environment writes, usage) rather than to `endpoints.ts`.
 */
import { request } from "./client";
import { networkError, problemToError } from "./problem";
import type { components } from "./contract";

type Schemas = components["schemas"];

export type McpServer = Schemas["McpServer"];
export type McpServerStatus = Schemas["McpServerStatus"];
export type McpLinkState = Schemas["McpLinkState"];
export type McpServerConnection = Schemas["McpServerConnection"];
export type McpEnvironment = Schemas["McpEnvironment"];
export type McpPairingSummary = Schemas["McpPairingSummary"];
export type McpCursorMeta = Schemas["McpCursorMeta"];

const base = (orgId: string) => `/v1/orgs/${encodeURIComponent(orgId)}/mcp`;

export async function listMcpServers(orgId: string, signal?: AbortSignal): Promise<McpServer[]> {
  const res = await request<McpServer[]>(`${base(orgId)}/servers`, signal ? { signal } : {});
  return res.data;
}

export async function listMcpServerConnections(
  orgId: string,
  serverId: string,
  options: { cursor?: string | null; limit?: number; signal?: AbortSignal } = {}
): Promise<{ items: McpServerConnection[]; nextCursor: string | null }> {
  const res = await request<McpServerConnection[], McpCursorMeta>(
    `${base(orgId)}/servers/${encodeURIComponent(serverId)}/connections`,
    {
      query: { cursor: options.cursor ?? undefined, limit: options.limit ?? 20 },
      ...(options.signal ? { signal: options.signal } : {})
    }
  );
  return { items: res.data, nextCursor: res.meta?.nextCursor ?? null };
}

/** Read-only here; requires `mcp.environments.manage`. */
export async function listMcpEnvironments(orgId: string, serverId: string, signal?: AbortSignal): Promise<McpEnvironment[]> {
  const res = await request<McpEnvironment[]>(
    `${base(orgId)}/servers/${encodeURIComponent(serverId)}/environments`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function unlinkMcpServer(orgId: string, serverId: string, idempotencyKey: string): Promise<McpServer> {
  const res = await request<McpServer>(`${base(orgId)}/servers/${encodeURIComponent(serverId)}/unlink`, {
    method: "POST",
    body: {},
    idempotencyKey
  });
  return res.data;
}

export async function resolveMcpPairing(orgId: string, userCode: string): Promise<McpPairingSummary> {
  const res = await request<McpPairingSummary>(`${base(orgId)}/pairings/resolve`, {
    method: "POST",
    body: { userCode }
  });
  return res.data;
}

export async function approveMcpPairing(
  orgId: string,
  pairingId: string,
  body: { userCode: string; replaceServerId?: string | null },
  idempotencyKey: string
): Promise<McpServer> {
  const res = await request<McpServer>(`${base(orgId)}/pairings/${encodeURIComponent(pairingId)}/approve`, {
    method: "POST",
    body: { userCode: body.userCode, ...(body.replaceServerId ? { replaceServerId: body.replaceServerId } : {}) },
    idempotencyKey
  });
  return res.data;
}

export async function denyMcpPairing(orgId: string, pairingId: string, userCode: string, idempotencyKey: string): Promise<McpPairingSummary> {
  const res = await request<McpPairingSummary>(`${base(orgId)}/pairings/${encodeURIComponent(pairingId)}/deny`, {
    method: "POST",
    body: { userCode },
    idempotencyKey
  });
  return res.data;
}

// ---- Seats (MCP_P_06) ---------------------------------------------------------------
// MCP seats are their own Entitlements pool (product `mcp`), separate from extension
// seats. Writes carry the MCP license ETag from `getMcpSeats` as If-Match; Entitlements
// problems (`seat_capacity_exceeded`, `version_conflict`, `mcp_license_required`, …)
// arrive unchanged as AppError codes.

export type McpSeatCandidate = Schemas["McpSeatCandidate"];
export type McpSeatsMeta = Schemas["McpSeatsEnvelope"]["meta"];
export type McpSeatWriteMeta = Schemas["McpSeatAssignmentEnvelope"]["meta"];

/**
 * Seat licensing has two kinds of seat (CONTRACT_NOTES N-32). A read-write seat includes
 * every read a read-only seat can do; a read-only seat can never write.
 *
 * `seatType` and the four per-type counters are OPTIONAL in the generated contract, so a
 * server that has not rolled out yet simply omits them. The three local shapes below make
 * them required and `normalizeSeats` fills them in once, at the boundary: an untyped seat
 * is read-write (which is what every seat sold before typed licensing is), and an untyped
 * licence is an all-read-write pool. Nothing downstream has to branch on "field missing".
 */
export type McpSeatType = Schemas["McpSeatType"];

type GeneratedSeatAssignment = Schemas["McpSeatAssignment"];
type GeneratedSeatLicense = Schemas["McpSeatLicense"];

export interface McpSeatAssignment extends GeneratedSeatAssignment {
  seatType: McpSeatType;
}

export interface McpSeatLicense extends GeneratedSeatLicense {
  readWriteSeatLimit: number;
  readOnlySeatLimit: number;
  readWriteAssigned: number;
  readOnlyAssigned: number;
}

export interface McpSeats extends Omit<Schemas["McpSeats"], "license" | "assignments"> {
  license: McpSeatLicense;
  assignments: McpSeatAssignment[];
}

function normalizeAssignment(assignment: GeneratedSeatAssignment): McpSeatAssignment {
  return { ...assignment, seatType: assignment.seatType === "readonly" ? "readonly" : "readwrite" };
}

function normalizeLicense(license: GeneratedSeatLicense): McpSeatLicense {
  return {
    ...license,
    readWriteSeatLimit: license.readWriteSeatLimit ?? license.seatLimit,
    readOnlySeatLimit: license.readOnlySeatLimit ?? 0,
    readWriteAssigned: license.readWriteAssigned ?? license.assigned,
    readOnlyAssigned: license.readOnlyAssigned ?? 0
  };
}

export interface McpSeatsResult extends McpSeats {
  meta: McpSeatsMeta;
}

export async function getMcpSeats(orgId: string, signal?: AbortSignal): Promise<McpSeatsResult> {
  const res = await request<Schemas["McpSeats"], McpSeatsMeta>(`${base(orgId)}/seats`, signal ? { signal } : {});
  return {
    ...res.data,
    license: normalizeLicense(res.data.license),
    assignments: res.data.assignments.map(normalizeAssignment),
    meta: res.meta
  };
}

/**
 * `ifMatch` is `license.etag` from the last `getMcpSeats`; `*` would ignore concurrent
 * changes. The same call assigns a seat and changes an existing seat's type: the PUT is
 * the seat's desired state, so `seatType` is always sent.
 */
export async function assignMcpSeat(
  orgId: string,
  userId: string,
  seatType: McpSeatType,
  ifMatch: string,
  idempotencyKey: string
): Promise<McpSeatAssignment> {
  const res = await request<GeneratedSeatAssignment, McpSeatWriteMeta>(`${base(orgId)}/seats/${encodeURIComponent(userId)}`, {
    method: "PUT",
    body: { seatType, ifMatch },
    ifMatch,
    idempotencyKey
  });
  return normalizeAssignment(res.data);
}

export async function revokeMcpSeat(orgId: string, userId: string, idempotencyKey: string): Promise<McpSeatAssignment> {
  const res = await request<GeneratedSeatAssignment, McpSeatWriteMeta>(`${base(orgId)}/seats/${encodeURIComponent(userId)}`, {
    method: "DELETE",
    idempotencyKey
  });
  return normalizeAssignment(res.data);
}

// ---- Hosted MCP (self-service provisioning) ----------------------------------------
// EpiDev can run the MCP server for an organization instead of the customer hosting it.
// `GET .../mcp/hosting` reports whether hosting is offered at all (`enabled`) plus the
// organization's deployments; the POST provisions one and answers `202` with that
// deployment. Provisioning is asynchronous: a deployment reads `provisioning` until the
// backend says otherwise, and the server itself only appears on `GET .../mcp/servers`
// once it links.

export type McpHostedDeployment = Schemas["McpHostedDeployment"];
export type McpHostedStatus = McpHostedDeployment["status"];
export type McpHosting = Schemas["McpHosting"];

export async function getMcpHosting(orgId: string, signal?: AbortSignal): Promise<McpHosting> {
  const res = await request<McpHosting>(`${base(orgId)}/hosting`, signal ? { signal } : {});
  return { enabled: res.data.enabled === true, deployments: res.data.deployments ?? [] };
}

/** Provisions one hosted server. Requires `mcp.manage`; CSRF and Idempotency-Key as usual. */
export async function createMcpHostedServer(orgId: string, name: string, idempotencyKey: string): Promise<McpHostedDeployment> {
  const res = await request<McpHostedDeployment>(`${base(orgId)}/hosting`, {
    method: "POST",
    body: { name },
    idempotencyKey
  });
  return res.data;
}

// ---------------------------------------------------------------- MCP_P_05 policy
// epidev/mcp-access-policy/v1 per server. The document itself is typed in
// $lib/domain/mcp-policy (the contract schema carries it as a free-form object).

export type McpPolicy = Schemas["McpPolicy"];
export type McpPolicyRule = Schemas["McpPolicyRule"];
export type McpPolicyPreset = Schemas["McpPolicyPreset"];
export type McpPolicyCatalog = Schemas["McpPolicyCatalog"];
export type McpPolicyRevision = Schemas["McpPolicyRevision"];
export type McpPolicyCall = Schemas["McpPolicyCall"];
export type McpPolicyEvaluation = Schemas["McpPolicyEvaluation"];
export type McpPolicyPublishResult = Schemas["McpPolicyPublishResult"];
export type McpPolicyDelivery = "pushed" | "pending_link";

export interface McpPolicyState {
  policy: McpPolicy | null;
  /** 0 before the first publish. */
  revision: number;
  delivery: McpPolicyDelivery | null;
  /** The If-Match for the next publish (strongEtag(revision)). */
  etag: string | null;
}

const serverPath = (orgId: string, serverId: string) => `${base(orgId)}/servers/${encodeURIComponent(serverId)}`;

export async function getMcpPolicy(orgId: string, serverId: string, signal?: AbortSignal): Promise<McpPolicyState> {
  const res = await request<McpPolicy | null, { revision: number; delivery: McpPolicyDelivery | null }>(
    `${serverPath(orgId, serverId)}/policy`, signal ? { signal } : {});
  const etag = res.etag && res.etag.startsWith("W/") ? res.etag.slice(2) : res.etag;
  return { policy: res.data, revision: res.meta?.revision ?? 0, delivery: res.meta?.delivery ?? null, etag };
}

export async function publishMcpPolicy(
  orgId: string,
  serverId: string,
  document: Record<string, unknown>,
  ifMatch: string,
  idempotencyKey: string
): Promise<McpPolicyPublishResult> {
  const res = await request<McpPolicyPublishResult>(`${serverPath(orgId, serverId)}/policy`, {
    method: "PUT",
    body: { document },
    ifMatch,
    idempotencyKey
  });
  return res.data;
}

/** Test a call against a draft (`document`) or, without one, the published revision. No side effects. */
export async function evaluateMcpPolicy(
  orgId: string,
  serverId: string,
  body: { document?: Record<string, unknown>; call: McpPolicyCall },
  signal?: AbortSignal
): Promise<McpPolicyEvaluation> {
  const res = await request<McpPolicyEvaluation>(`${serverPath(orgId, serverId)}/policy/evaluate`, {
    method: "POST",
    body,
    ...(signal ? { signal } : {})
  });
  return res.data;
}

export async function listMcpPolicyHistory(
  orgId: string,
  serverId: string,
  options: { limit?: number; beforeRevision?: number | null; signal?: AbortSignal } = {}
): Promise<{ items: McpPolicyRevision[]; nextBeforeRevision: number | null }> {
  const res = await request<McpPolicyRevision[], { nextBeforeRevision: number | null }>(`${serverPath(orgId, serverId)}/policy/history`, {
    query: { limit: options.limit ?? 5, beforeRevision: options.beforeRevision ?? undefined },
    ...(options.signal ? { signal: options.signal } : {})
  });
  return { items: res.data, nextBeforeRevision: res.meta?.nextBeforeRevision ?? null };
}

export async function listMcpPolicyPresets(orgId: string, signal?: AbortSignal): Promise<McpPolicyPreset[]> {
  const res = await request<McpPolicyPreset[]>(`${base(orgId)}/policy-presets`, signal ? { signal } : {});
  return res.data;
}

export async function getMcpPolicyCatalog(orgId: string, serverId: string | null, signal?: AbortSignal): Promise<McpPolicyCatalog> {
  const res = await request<McpPolicyCatalog>(`${base(orgId)}/policy-catalog`, {
    query: { serverId: serverId ?? undefined },
    ...(signal ? { signal } : {})
  });
  return res.data;
}

// ---------------------------------------------------------------- MCP_P_08 usage
// Redacted usage events (identifiers only) with the same filters on the list, the
// summary, and the CSV export. A member without `mcp.manage` gets their own events only.

export type McpUsageEvent = Schemas["McpUsageEvent"];
export type McpUsageSummary = Schemas["McpUsageSummary"];
export type McpUsageOutcome = Schemas["McpUsageOutcome"];
export type McpUsageDecision = Schemas["McpUsageDecision"];
export type McpWriteClass = Schemas["McpWriteClass"];

export interface McpUsageQuery {
  subject?: string;
  env?: string;
  tool?: string;
  decision?: McpUsageDecision;
  writeClass?: McpWriteClass;
  outcome?: McpUsageOutcome;
  serverId?: string;
  /** ISO 8601, inclusive. */
  from?: string;
  /** ISO 8601, exclusive. */
  to?: string;
}

function usageQuery(filters: McpUsageQuery): Record<string, string | undefined> {
  return {
    subject: filters.subject, env: filters.env, tool: filters.tool, decision: filters.decision, writeClass: filters.writeClass,
    outcome: filters.outcome, serverId: filters.serverId, from: filters.from, to: filters.to
  };
}

export async function listMcpUsage(
  orgId: string,
  filters: McpUsageQuery,
  options: { cursor?: string | null; limit?: number; signal?: AbortSignal } = {}
): Promise<{ items: McpUsageEvent[]; nextCursor: string | null }> {
  const res = await request<McpUsageEvent[], McpCursorMeta>(`${base(orgId)}/usage`, {
    query: { ...usageQuery(filters), cursor: options.cursor ?? undefined, limit: options.limit ?? 50 },
    ...(options.signal ? { signal: options.signal } : {})
  });
  return { items: res.data, nextCursor: res.meta?.nextCursor ?? null };
}

export async function getMcpUsageSummary(orgId: string, filters: McpUsageQuery, signal?: AbortSignal): Promise<McpUsageSummary> {
  const res = await request<McpUsageSummary>(`${base(orgId)}/usage/summary`, {
    query: usageQuery(filters),
    ...(signal ? { signal } : {})
  });
  return res.data;
}

export interface McpUsageExport {
  blob: Blob;
  filename: string;
  /** Rows the API matched when the export started. */
  rows: number | null;
}

/**
 * `format=csv` with the current filters. Not through `request()`: the body is CSV, not
 * an envelope. Problems (`usage_export_too_large`, `usage_range_too_wide`, …) arrive as
 * AppError like every other call.
 */
export async function exportMcpUsageCsv(orgId: string, filters: McpUsageQuery): Promise<McpUsageExport> {
  const correlationId = crypto.randomUUID();
  const params = new URLSearchParams({ format: "csv" });
  for (const [key, value] of Object.entries(usageQuery(filters))) if (value) params.set(key, value);
  let response: Response;
  try {
    response = await fetch(`${base(orgId)}/usage?${params.toString()}`, {
      credentials: "include",
      mode: "same-origin",
      headers: { accept: "text/csv, application/problem+json", "x-epidev-request-id": correlationId }
    });
  } catch {
    throw networkError(correlationId);
  }
  const serverCorrelation = response.headers.get("x-request-id") || correlationId;
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      /* problem body may be absent */
    }
    const retryAfter = Number(response.headers.get("retry-after"));
    throw problemToError(body, response.status, serverCorrelation, Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined);
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "mcp-usage.csv";
  const rows = Number(response.headers.get("x-epidev-export-rows"));
  return { blob: await response.blob(), filename, rows: Number.isFinite(rows) && response.headers.has("x-epidev-export-rows") ? rows : null };
}

// ---------------------------------------------------------------- MCP_P_07 environments
// ERP environment metadata per server, plus credentials sealed in the browser
// ($lib/crypto/mcp-sealing) that the portal relays to the server and never stores. A PUT
// to an offline server is refused (`server_offline`), never queued; a refusal by the
// server itself comes back as `result.ok: false` with the server's code.

export type McpServerPublicKey = Schemas["McpServerPublicKey"];
export type McpEnvironmentMetadataInput = Schemas["McpEnvironmentMetadataInput"];
export type McpEnvironmentUpsert = Schemas["McpEnvironmentUpsert"];
export type McpEnvironmentResult = Schemas["McpEnvironmentResult"];
export type McpSealedCredentialsPayload = Schemas["McpSealedCredentials"];
export type McpEnvironmentDeleted = Schemas["McpEnvironmentDeleteEnvelope"]["data"];

const environmentPath = (orgId: string, serverId: string, name: string) =>
  `${serverPath(orgId, serverId)}/environments/${encodeURIComponent(name)}`;

export async function getMcpServerPublicKey(orgId: string, serverId: string, signal?: AbortSignal): Promise<McpServerPublicKey> {
  const res = await request<McpServerPublicKey>(`${serverPath(orgId, serverId)}/public-key`, signal ? { signal } : {});
  return res.data;
}

export async function getMcpEnvironment(orgId: string, serverId: string, name: string, signal?: AbortSignal): Promise<McpEnvironment> {
  const res = await request<McpEnvironment>(environmentPath(orgId, serverId, name), signal ? { signal } : {});
  return res.data;
}

/** `sealedCredentials` must be sealed for this exact `serverId` and `environment.name`. */
export async function putMcpEnvironment(
  orgId: string,
  serverId: string,
  body: { environment: McpEnvironmentMetadataInput & { name: string }; sealedCredentials?: McpSealedCredentialsPayload; expectedVersion?: number | null },
  idempotencyKey: string
): Promise<McpEnvironmentUpsert> {
  const res = await request<McpEnvironmentUpsert>(environmentPath(orgId, serverId, body.environment.name), {
    method: "PUT",
    body,
    idempotencyKey
  });
  return res.data;
}

/** Removes the portal's metadata only; the server's own configuration is unchanged. */
export async function deleteMcpEnvironment(orgId: string, serverId: string, name: string, idempotencyKey: string): Promise<McpEnvironmentDeleted> {
  const res = await request<McpEnvironmentDeleted>(environmentPath(orgId, serverId, name), { method: "DELETE", idempotencyKey });
  return res.data;
}

// ---------------------------------------------------------------- token minting (plan §4.6)
// A short-lived bearer token for the caller, minted by the server itself over the link and
// returned once (`Cache-Control: no-store`). Keep it in component state only: never in a
// store, storage, the URL, or telemetry. A replayed Idempotency-Key is refused with
// `mcp_token_already_issued` because the portal keeps no copy to replay.

export type McpToken = Schemas["McpToken"];
export type McpTokenRequest = Schemas["McpTokenRequest"];

export async function mintMcpToken(orgId: string, serverId: string, body: McpTokenRequest, idempotencyKey: string): Promise<McpToken> {
  const res = await request<McpToken>(`${serverPath(orgId, serverId)}/tokens`, {
    method: "POST",
    body: {
      ...(body.ttlSeconds === undefined ? {} : { ttlSeconds: body.ttlSeconds }),
      ...(body.environment ? { environment: body.environment } : {})
    },
    idempotencyKey
  });
  return res.data;
}

// ---------------------------------------------------------------- server settings
// The public URL MCP clients reach the server at (`<publicUrl>/mcp`). Owners and admins
// set or clear it; the API refuses anything but an https origin or an http loopback
// origin with `invalid_mcp_public_url`. No Idempotency-Key: the write is a plain set.

export type McpServerUpdateRequest = Schemas["McpServerUpdateRequest"];

export async function updateMcpServer(orgId: string, serverId: string, body: McpServerUpdateRequest): Promise<McpServer> {
  const res = await request<McpServer>(serverPath(orgId, serverId), { method: "PATCH", body: { publicUrl: body.publicUrl } });
  return res.data;
}

// ---------------------------------------------------------------- OAuth sign-in (consent + connected apps)
// MCP clients (Claude Code, Codex) sign in with OAuth: the portal is the authorization
// server of each MCP server. `/oauth/<serverId>/authorize` sends the browser to the SPA's
// `/mcp/authorize?request=<id>` consent page, which reads, approves, or denies the pending
// request here. Approve and deny answer the client's redirect URI (`redirectTo`), which is
// off-origin by design (usually a loopback port the client listens on). None of these
// writes take an Idempotency-Key: an authorization code is single-use, so a replay is 410.

export type McpOAuthRequest = Schemas["McpOAuthRequest"];
export type McpOAuthBlocker = NonNullable<McpOAuthRequest["blocker"]>;
export type McpOAuthRedirect = Schemas["McpOAuthRedirectEnvelope"]["data"];
export type McpOAuthSession = Schemas["McpOAuthSession"];
export type McpOAuthSessionsMeta = Schemas["McpOAuthSessionListEnvelope"]["meta"];
export type McpOAuthOverCap = McpOAuthSessionsMeta["overCap"][number];

const oauthRequestPath = (requestId: string) => `/v1/mcp/oauth/requests/${encodeURIComponent(requestId)}`;

/** Organization-agnostic; `404 mcp_oauth_request_not_found`, `410 mcp_oauth_request_expired`. */
export async function getMcpOAuthRequest(requestId: string, signal?: AbortSignal): Promise<McpOAuthRequest> {
  const res = await request<McpOAuthRequest>(oauthRequestPath(requestId), signal ? { signal } : {});
  return res.data;
}

export async function approveMcpOAuthRequest(requestId: string): Promise<McpOAuthRedirect> {
  const res = await request<McpOAuthRedirect>(`${oauthRequestPath(requestId)}/approve`, { method: "POST" });
  return res.data;
}

export async function denyMcpOAuthRequest(requestId: string): Promise<McpOAuthRedirect> {
  const res = await request<McpOAuthRedirect>(`${oauthRequestPath(requestId)}/deny`, { method: "POST" });
  return res.data;
}

/** Managers get every member's sign-ins; a seated member gets their own (filtered by the API). */
export async function listMcpOAuthSessions(
  orgId: string,
  options: { serverId?: string | null; signal?: AbortSignal } = {}
): Promise<{ items: McpOAuthSession[]; meta: McpOAuthSessionsMeta }> {
  const res = await request<McpOAuthSession[], McpOAuthSessionsMeta>(`${base(orgId)}/oauth-sessions`, {
    query: { serverId: options.serverId ?? undefined },
    ...(options.signal ? { signal: options.signal } : {})
  });
  return { items: res.data, meta: { sessionCap: res.meta?.sessionCap ?? 0, overCap: res.meta?.overCap ?? [] } };
}

/** `serverNotified: false` means the server was offline: revoked here, told on reconnect. */
export async function revokeMcpOAuthSession(orgId: string, sessionId: string): Promise<McpOAuthSession> {
  const res = await request<McpOAuthSession>(`${base(orgId)}/oauth-sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
  return res.data;
}
